'use client';

import { Suspense, useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';
import { PostHogProvider as PHProvider, usePostHog } from 'posthog-js/react';
import {
  redactSensitiveQueryParams,
  sanitizePostHogEvent,
} from '@/lib/posthog-sanitize';
import { resolveEnvironment } from '@/lib/posthog/standard-properties';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com';
// Used by posthog-js to build links back to PostHog (session-replay URLs in
// the console, etc.). When NEXT_PUBLIC_POSTHOG_HOST is a reverse proxy like
// `t.fountain.com`, this must be the real PostHog UI host or the links 404.
const POSTHOG_UI_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_UI_HOST ?? 'https://us.posthog.com';

// Init whenever a project API key is present. Per-environment project
// separation in PostHog (Fountain NonProd vs Prod) — not an env-name check —
// is the safety story; the LaunchDarkly kill switch handles prod ramp.
if (typeof window !== 'undefined' && POSTHOG_KEY) {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: POSTHOG_UI_HOST,
    // Pin the SDK's default-config snapshot so PostHog can ship new defaults
    // without silently changing our behaviour. Bump deliberately.
    defaults: '2026-01-30',
    // App Router needs manual pageviews; we fire them in PostHogPageview.
    capture_pageview: false,
    capture_pageleave: true,
    // Off, matching every other Fountain frontend. This surface was the only
    // one with it on, and produced all of the project's autocapture volume for
    // it. An autocaptured click cannot be named, breaks on the next markup
    // change, and nothing queries these; a named event survives both.
    autocapture: false,
    person_profiles: 'identified_only',
    // Flags come from LaunchDarkly. This skips the /flags call on load;
    // remote config, which session recording reads, still loads.
    advanced_disable_feature_flags: true,
    session_recording: {
      maskAllInputs: true,
      // Mask any element opted out via data-ph-no-capture or .ph-no-capture
      maskTextSelector: '[data-ph-no-capture], .ph-no-capture',
    },
    before_send: sanitizePostHogEvent,
    loaded: (ph) => {
      if (process.env.NODE_ENV === 'development') {
        ph.debug();
      }
    },
  });

  // The properties every browser event carries, registered once rather than
  // typed at each call site. `register` puts them on every subsequent capture,
  // `$pageview` included — which is what makes development traffic separable
  // from a customer's.
  //
  // `is_internal_user` is deliberately absent: the browser cannot tell staff
  // from a customer. The server path in lib/posthog/track.ts sets it, where the
  // answer is known.
  posthog.register({
    app: 'hire-go',
    surface: 'frontend',
    environment: resolveEnvironment(),
  });
}

function PostHogPageviewInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const ph = usePostHog();

  useEffect(() => {
    if (!ph || !pathname) {
      return;
    }
    const url = searchParams?.toString()
      ? `${pathname}?${searchParams.toString()}`
      : pathname;
    ph.capture('$pageview', {
      $current_url: redactSensitiveQueryParams(window.location.origin + url),
    });
  }, [pathname, searchParams, ph]);

  return null;
}

function PostHogPageview() {
  // useSearchParams() opts the route into client rendering unless wrapped
  // in Suspense. https://nextjs.org/docs/messages/deopted-into-client-rendering
  return (
    <Suspense fallback={null}>
      <PostHogPageviewInner />
    </Suspense>
  );
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  if (!POSTHOG_KEY) {
    return <>{children}</>;
  }
  return (
    <PHProvider client={posthog}>
      <PostHogPageview />
      {children}
    </PHProvider>
  );
}
